import { BaseReplayController } from '@bn-games/history-core';
import { GameState } from '../game/GameState.js';
import { getRuleSet, withReplayLimits } from '../game/rules/index.js';
import type { Move, Position } from '../game/types.js';
import { recordOutcome } from './record.js';
import type { GameRecord, RecordedMove } from './types.js';

export interface ReplayStep {
  readonly index: number;
  readonly recorded: RecordedMove;
  readonly move: Move;
  /** The position the move was played from (the animator needs the pre-move board). */
  readonly before: Position;
}

/**
 * Read-only replay over a recorded game. Every position is rebuilt once, up
 * front, so stepping and seeking in either direction are array lookups; the
 * last one carries the recorded result, including endings the board cannot
 * show (resignation, agreement, flag).
 */
export class ReplayController extends BaseReplayController {
  private constructor(
    private readonly record: GameRecord,
    /** `states[i]` is the game after `i` moves. */
    private readonly states: readonly GameState[],
  ) {
    super();
  }

  /** Null when the record does not replay under today's rules. */
  static open(record: GameRecord): ReplayController | null {
    try {
      // Already played: replay under the automatic limits, as the recorder's game was.
      let state = GameState.fromFen(record.startFen, withReplayLimits(getRuleSet(record.variant)));
      const states = [state];
      for (const recorded of record.moves) {
        const next = state.playUci(recorded.uci);
        if (next === null) return null;
        states.push(next);
        state = next;
      }
      if (state.outcome === null) states[states.length - 1] = state.conclude(recordOutcome(record));
      return new ReplayController(record, states);
    } catch {
      return null;
    }
  }

  getRecord(): GameRecord {
    return this.record;
  }

  getState(): GameState {
    const state = this.states[this.stepIndex] ?? this.states[0];
    if (state === undefined) throw new Error('ReplayController: no positions');
    return state;
  }

  /** Every position of the game, `[i]` after `i` moves. */
  getStates(): readonly GameState[] {
    return this.states;
  }

  getTotalSteps(): number {
    return this.record.moves.length;
  }

  /** Advance one move, returning what the scene needs to animate it; null at the end. */
  stepForward(): ReplayStep | null {
    const index = this.stepIndex;
    const before = this.states[index];
    const move = this.states[index + 1]?.lastMove;
    const recorded = this.record.moves[index];
    if (before === undefined || move === undefined || recorded === undefined) return null;
    this.advanceIndex();
    return { index, recorded, move, before: before.position };
  }

  // Positions are precomputed, so the base class's rebuild-on-seek has nothing to redo.
  protected reset(): void {}

  protected applyStep(): void {}
}
